// SessionSidebar.tsx
import React, { useState, useCallback } from 'react';
import { Plus, MessageSquare, ArrowLeftToLine, ArrowRightToLine } from 'lucide-react';
import '../../../../scss/SessionSidebar.scss';
import { ChatSession } from "../../../types/chat.ts";

interface SessionSidebarProps {
    sessions: Array<ChatSession>;
    currentSessionId: string | null;
    onSessionSelect: (sessionId: string) => void;
    onNewChat: () => void;
    isVisible: boolean;
    onMouseLeave?: () => void;
    onLoadMore: (page: number) => Promise<void>;
    hasMoreSessions: boolean;
    isLoadingMore?: boolean;
    onShowLess: () => void;
    setSessions: React.Dispatch<React.SetStateAction<ChatSession[]>>;
    setHasMoreSessions: React.Dispatch<React.SetStateAction<boolean>>;
    isPinned: boolean;
    onPinToggle: () => void;
}

const SessionSidebar: React.FC<SessionSidebarProps> = ({
                                                           sessions,
                                                           currentSessionId,
                                                           onSessionSelect,
                                                           onNewChat,
                                                           isVisible,
                                                           onMouseLeave,
                                                           onLoadMore,
                                                           hasMoreSessions,
                                                           isLoadingMore = false,
                                                           onShowLess,
                                                           isPinned = false,
                                                           onPinToggle
                                                       }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedSessionId, setSelectedSessionId] = useState<string | null>(currentSessionId);

    // Update local selection state when prop changes
    React.useEffect(() => {
        if (currentSessionId !== selectedSessionId) {
            setSelectedSessionId(currentSessionId);
        }
    }, [currentSessionId]);

    const getSessionName = useCallback((session: ChatSession) => {
        // First check for first message
        if (session.firstMessage) {
            const message = session.firstMessage.trim();

            // Take first 50 characters
            if (message.length <= 50) {
                return message;
            }

            // If longer than 50, cut at last word boundary
            const truncated = message.substring(0, 50);
            const lastSpace = truncated.lastIndexOf(' ');

            if (lastSpace > 0) {
                return `${truncated.substring(0, lastSpace)}...`;
            }

            return `${truncated}...`;
        }

        // If no first message but has a name that isn't default
        if (session.name && session.name !== 'New chat') {
            return session.name;
        }

        // Default case
        return 'New chat';
    }, []);

    const formatDate = useCallback((dateString: string) => {
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'Just now';

            const now = new Date();
            const yesterday = new Date(now);
            yesterday.setDate(now.getDate() - 1);

            const formatTime = (d: Date) =>
                d.toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                });

            if (date.toDateString() === now.toDateString()) {
                return formatTime(date);
            }

            if (date.toDateString() === yesterday.toDateString()) {
                return `Yesterday ${formatTime(date)}`;
            }

            const daysAgo = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
            if (daysAgo < 7) {
                return date.toLocaleDateString('en-US', { weekday: 'long' });
            }

            if (date.getFullYear() === now.getFullYear()) {
                return date.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric'
                });
            }

            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            });
        } catch {
            return 'Just now';
        }
    }, []);

    const handleLoadMore = async () => {
        if (isLoadingMore) return;
        const nextPage = currentPage + 1;
        await onLoadMore(nextPage);
        setCurrentPage(nextPage);
    };

    const handleSessionSelect = (sessionId: string) => {
        console.log('[Debug] Session selected:', {
            sessionId,
            currentSessionId: selectedSessionId
        });
        setSelectedSessionId(sessionId);
        onSessionSelect(sessionId);
    };

    const sortedSessions = [...sessions].sort(
        (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
    );

    return (
        <div
            className={`session-sidebar ${isVisible ? 'show' : ''} ${isPinned ? 'pinned' : ''}`}
            onMouseLeave={onMouseLeave}
            style={{visibility: isVisible ? 'visible' : 'hidden'}}
        >
            <div className="sidebar-header">
                <div className="d-flex align-items-center position-relative w-100">
                    <h6 className="mb-0">Chat History</h6>
                    <button
                        onClick={onPinToggle}
                        className="btn btn-link pin-toggle-btn position-absolute end-0"
                        title={isPinned ? "Collapse sidebar" : "Expand sidebar"}
                    >
                        {isPinned ? <ArrowLeftToLine size={20}/> : <ArrowRightToLine size={20}/>}
                    </button>
                </div>
            </div>

            <div className="sessions-container">
                <div className="sessions-list">
                    <div className="pagination-controls mb-2">
                        <button
                            onClick={onNewChat}
                            className="pagination-btn"
                        >
                            <Plus size={16}/>
                            New Chat
                        </button>
                    </div>

                    <div className="sessions-label">Recent Chats</div>

                    {sortedSessions.map((session) => (
                        <button
                            key={session.id}
                            onClick={() => handleSessionSelect(session.id)}
                            className={`session-item ${session.id === selectedSessionId ? 'active' : ''}`}
                            type="button"
                        >
                            <MessageSquare size={16} className="message-icon"/>
                            <div className="session-content">
                                <div className="session-name">
                                    {getSessionName(session)}
                                </div>
                                <div className="session-date">
                                    {formatDate(session.updated_at)}
                                </div>
                            </div>
                        </button>
                    ))}

                    {sessions.length === 0 && (
                        <div className="text-center text-muted py-4">
                            No chats found
                        </div>
                    )}

                    {sessions.length >= 10 && (
                        <div className="pagination-controls">
                            {currentPage > 1 && (
                                <button
                                    onClick={() => {
                                        onShowLess();
                                        setCurrentPage(1);
                                    }}
                                    className="pagination-btn"
                                >
                                    Show Less
                                </button>
                            )}
                            {hasMoreSessions && (
                                <button
                                    onClick={handleLoadMore}
                                    className="pagination-btn"
                                    disabled={isLoadingMore}
                                >
                                    {isLoadingMore ? 'Loading...' : 'Show More'}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SessionSidebar;